import { IContainerYardImages, IPavedImages, IAirportImagesType, IRailwayImagesType } from '../types';
import { logError, LogTag } from '../../../utils/utils';
import pdfService from '../../../services/PdfImageCaptureService';

/**
 * Extract container yard / unpaved images with proper fallbacks
 */
export const getContainerYardImages = (images: unknown): IContainerYardImages | null => {
    if (!images || typeof images !== 'object') return null;

    const typedImages = images as Record<string, unknown>;
    return {
        tireImg1: String(typedImages.tireImg1 || ''),
        tireImg2: String(typedImages.tireImg2 || ''),
        tireImg3: String(typedImages.tireImg3 || ''),
        chartImg: String(typedImages.chartImg || ''),
    };
};

/**
 * Get Unpaved images directly from sessionStorage
 */
export const getUnpavedImages = (projectGuid?: string): IContainerYardImages | null => {
    if (!projectGuid) return null;

    try {
        const storageKey = `unpavedPdfImages_${projectGuid}`;
        const storedImages = sessionStorage.getItem(storageKey);
        if (!storedImages) return null;

        const imageData = JSON.parse(storedImages) as Record<string, unknown>;
        return {
            tireImg1: String(imageData.tireImg1 || ''),
            tireImg2: String(imageData.tireImg2 || ''),
            tireImg3: String(imageData.tireImg3 || ''),
            chartImg: String(imageData.chartImg || ''),
        };
    } catch (error) {
        logError('imageExtractors', 'Error loading Unpaved images from sessionStorage:', error, LogTag.PDF_EXPORT);
        return null;
    }
};

/**
 * Extract paved images with proper typing
 */
export const getPavedImages = (images: unknown): IPavedImages | null => {
    if (!images || typeof images !== 'object') return null;

    const typedImages = images as Record<string, unknown>;

    return {
        tireImageUnreinforced: String(typedImages.tireImageUnreinforced || ''),
        tireImageReinforced: String(typedImages.tireImageReinforced || ''),
        tireImageGeogrid: String(typedImages.tireImageGeogrid || ''),
        chartImg: String(typedImages.chartImg || ''),
        // Include trafficData if present
        trafficData: typedImages.trafficData as IPavedImages['trafficData'],
        // Include dynamic design sections if present
        surfaceDesignImages: typedImages.surfaceDesignImages as IPavedImages['surfaceDesignImages'],
        baseDesignImages: typedImages.baseDesignImages as IPavedImages['baseDesignImages'],
        subbaseDesignImages: typedImages.subbaseDesignImages as IPavedImages['subbaseDesignImages'],
    };
};

/**
 * Extract airport images with proper key mapping
 */
export const getAirportImages = (images: unknown): IAirportImagesType | null => {
    if (!images || typeof images !== 'object') return null;

    const typedImages = images as Record<string, unknown>;

    // Debug log to see what getAirportImages receives
    console.log('[getAirportImages:dynamic_canvas] Input data:', {
        hasImages: !!images,
        imageType: typeof images,
        keys: images && typeof images === 'object' ? Object.keys(images) : [],
        hasSurfaceDesignImages: !!typedImages.surfaceDesignImages,
        hasBaseDesignImages: !!typedImages.baseDesignImages,
        hasSubbaseDesignImages: !!typedImages.subbaseDesignImages,
    });

    return {
        tireImageUnstabilized: String(typedImages.tireImageUnstabilized || ''),
        tireImageIncreasedPassages: String(typedImages.tireImageIncreasedPassages || ''),
        tireImageGeogrid: String(typedImages.tireImageGeogrid || ''),
        chartImg: String(typedImages.airportChartImage || typedImages.chartImg || ''),
        // Include dynamic design sections if present
        surfaceDesignImages: typedImages.surfaceDesignImages as IAirportImagesType['surfaceDesignImages'],
        baseDesignImages: typedImages.baseDesignImages as IAirportImagesType['baseDesignImages'],
        subbaseDesignImages: typedImages.subbaseDesignImages as IAirportImagesType['subbaseDesignImages'],
    };
};

/**
 * Extract railway images with standard keys
 */
export const getRailwayImages = (images: unknown): IRailwayImagesType | null => {
    if (!images || typeof images !== 'object') return null;

    const typedImages = images as Record<string, unknown>;
    return {
        tireImageUnreinforced: String(typedImages.tireImageUnreinforced || ''),
        tireImageReinforced: String(typedImages.tireImageReinforced || ''),
    };
};

/**
 * Get stored paved images from sessionStorage with error handling
 */
export const getStoredPavedImages = (projectGuid?: string): IPavedImages | null => {
    if (!projectGuid) return null;

    try {
        return pdfService.getStoredPavedImages(projectGuid);
    } catch (error) {
        logError('imageExtractors', 'Error loading stored paved images:', error, LogTag.PDF_EXPORT);
        return null;
    }
};

/**
 * Image extractors object for backward compatibility
 */

/**
 * ✅ CRITICAL FIX BUG-005: Get stored Railway images from sessionStorage
 * Previously MISSING - Railway only had props transformer, no sessionStorage retrieval
 * 
 * @param projectGuid - GUID del progetto Railway
 * @returns Oggetto con immagini Railway o null se non trovate
 */
export const getStoredRailwayImages = (projectGuid?: string): IRailwayImagesType | null => {
    if (!projectGuid) return null;

    try {
        return pdfService.getStoredRailwayImages(projectGuid);
    } catch (error) {
        logError('imageExtractors', 'Error loading stored Railway images:', error, LogTag.PDF_EXPORT);
        return null;
    }
};
export const imageExtractors = {
    getContainerYardImages,
    getUnpavedImages: (projectGuid?: string) => getUnpavedImages(projectGuid),
    getPavedImages,
    getAirportImages,
    getRailwayImages,
    // ✅ NEW: Railway sessionStorage retrieval (BUG-005 fix)
    getStoredRailwayImages,
};
